import angular from 'angular';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { FactoryFormsModule } from '@b2b/common/factories/forms/forms.factory';
import { ITimeoutService } from 'angular';
import { TODO, UIBModal, createUIBModalStub, input } from '@b2b/types/core';
import { CartOrderForm } from '@b2b/types/cart';
import { isMembershipOrderForm } from '@b2b/types/membership';
import { CartFormsService, CartFormsServiceModule } from '../../services/cart-forms.service';
import { CartFormsModalsFilloutData, FormDefinitionElement, FormDetails, FormField, FormResponsePayload } from '../../types';
import { processResponse, validateCheckboxGroupAndRadioGroupRequired } from '../../utils/form-response.utils';

class CartFormsModalsFilloutController { 

  static $inject = ['NoticeFactory', 'CartFormsService', 'FormsFactory', '$timeout'];

  modalInstance = input.required<UIBModal<TODO>>(createUIBModalStub<any>(undefined));
  resolve = input.required<{ MODAL_DATA: CartFormsModalsFilloutData }>({
    MODAL_DATA: { form: {} as CartOrderForm },
  });

  // instances
  notice = new this.noticeFactory();
  formInstance = new this.formsFactory();

  // loaders
  loadingFormDetails = false;

  // data
  formDetails = {} as FormDetails;
  markupContainerId = 'cart-forms-modals-fillout-markup';

  get form() {
    return this.resolve.MODAL_DATA.form;
  }

  get isEventForm() {
    return this.form.itemType === 'event';
  }

  get isMembershipForm() {
    return isMembershipOrderForm(this.form);
  }

  get relatedItemNames(): string[] {
    return this.resolve.MODAL_DATA.relatedItemNames ?? [];
  }

  constructor(
    private noticeFactory: NoticeFactory,
    private cartFormsService: CartFormsService,
    private formsFactory: TODO,
    private $timeout: ITimeoutService,
  ) {
    // noop
  }

  $onInit() {
    this.formInstance.init();
    this.loadFormDetails();
  }

  $onChanges() {
    // Handle changes if needed
  }

  close() {
    this.modalInstance.dismiss('cancel');
  }

  loadFormDetails() {
    this.loadingFormDetails = true;
    this.cartFormsService.fetchFormDetails(this.resolve.MODAL_DATA.form.id).then((response) => {
      this.formDetails = response.data.payload;

      const existingResponse = this.resolve.MODAL_DATA.existingResponse;
      const sourceFormDefinition = existingResponse
        ? this.prefillSourceFormDefinition(this.formDetails.sourceFormDefinition, existingResponse.responseData.fields)
        : this.formDetails.sourceFormDefinition;

      this.formInstance.renderForm({
        containerId: this.markupContainerId,
        customFields: [],
        sourceFormDefinition,
      });

      if (existingResponse) {
        this.restoreOtherValues(existingResponse.responseData.fields);
      }
    }).catch(() => {
      this.notice.showErrorMessages([{ message: 'Failed to load form details.' }], { duration: 3000 });
    })
    .finally(() => {
      this.loadingFormDetails = false;
    });
  }

  /**
   * Injects existing response values into the form definition JSON
   * before passing it to formRender, so fields are pre-populated.
   * Mirrors the pattern in OrdersDetailFormsEditController.updateForm().
   */
  private prefillSourceFormDefinition(sourceFormDefinition: string, fields: FormField[]): string {
    let formDefinition: TODO[];
    try {
      formDefinition = JSON.parse(sourceFormDefinition);
    } catch {
      return sourceFormDefinition;
    }

    for (const responseField of fields) {
      const fieldName = responseField.name.replace('[]', '');
      const defField = formDefinition.find((f) => f.name === fieldName);
      if (!defField) continue;

      const responseValues = responseField.value.split(',');

      switch (defField.type) {
        case 'checkbox':
        case 'radio':
          if (responseField.value) defField.checked = true;
          break;
        case 'radio-group':
        case 'select':
        case 'checkbox-group':
          for (const opt of defField.values ?? []) {
            delete opt.selected;
          }
          for (const opt of defField.values ?? []) {
            if (responseValues.includes(opt.value) || responseValues.includes(` ${opt.value}`)) {
              opt.selected = true;
              if (defField.type === 'radio-group') break;
            }
          }
          break;
        default:
          defField.value = responseField.value;
      }
    }

    return JSON.stringify(formDefinition);
  }

  /**
   * After the form renders, click the "other" option and restore its value
   * for any field that had an otherValue in the previous response.
   */
  private restoreOtherValues(fields: FormField[]) {
    this.$timeout(() => {
      for (const field of fields) {
        if (!Object.prototype.hasOwnProperty.call(field, 'otherValue')) continue;
        const fieldName = field.name.replace('[]', '');
        $(`#${fieldName}-other`).trigger('click');
        $(`#${fieldName}-other-value`).val(field.otherValue ?? '').show();
      }
    }, 100);
  }

  submit() {
    if (this.loadingFormDetails) {
      return;
    }

    const data = $('#cart-forms-modals-fillout-container').serializeArray() as { name: string; value: string }[];

    /**
     * Unchecked checkboxes are not automatically serialized
     * so we need to do this manually
     */
    $('#orders-forms-markup input:checkbox').map((_index, element) => {
      const inputElement = element as HTMLInputElement;
      if (!inputElement.checked) {
        data.push({ name: inputElement.name, value: '' });
      }
    });

    const formDefinition = JSON.parse(this.formDetails.sourceFormDefinition || '[]') as FormDefinitionElement[];
    const validationErrors = validateCheckboxGroupAndRadioGroupRequired(data, formDefinition);

    if (validationErrors.length > 0) {
      this.notice.showErrorMessages(validationErrors.map((message) => ({ message })));
      return;
    }

    const processedResponse = processResponse(data);

    const props: FormResponsePayload = {
      formId: this.formDetails.id,
      name: this.formDetails.name,
      type: this.formDetails.type,
      source: this.formDetails.source,
      number: this.formDetails.number,
      sourceFormId: this.formDetails.sourceFormId,
      customFields: processedResponse.customFields,
      responseData: processedResponse.responseData,
      submissionDate: new Date().toISOString(),
      formCategoryType: this.form?.itemType,
    };

    const existingResponse = this.resolve.MODAL_DATA.existingResponse;
    const request = existingResponse?.id
      ? this.cartFormsService.updateFormResponse(existingResponse.id, props)
      : this.cartFormsService.submitFormResponse(props);

    request.then((response) => {
      this.cartFormsService.saveFormResponse(response.data.payload);
      this.notice.showSuccessMessages([{ message: 'Form submitted successfully.' }]);
      this.modalInstance.close(response.data.payload);
    }).catch(() => {
      this.notice.showErrorMessages([{ message: 'Failed to submit form data.' }]);
    });
  }
}

export const CartFormsModalFilloutComponentModule = angular
  .module('acme.view.application.cart.components.cart-forms.modals.fillout', [
    'ui.bootstrap',
    'acme.common.factory.notice',
    NoticesComponentModule,
    FactoryFormsModule,
    CartFormsServiceModule
  ])
  .component('acmeCartFormsModalFillout', {
    templateUrl:
      'views/application/views/cart/components/cart-forms/modals/fillout/cart-forms-modals-fillout.tpl.html',
    controller: CartFormsModalsFilloutController,
    bindings: {
      modalInstance: '<',
      resolve: '<',
    },
  }).name;